import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import { DeviceAPI } from "../../services/device.service";
import { getDeviceId } from "../../utils/device-id";
import { ApiError, NotAuthenticatedError } from "../../services/api.errors";

interface Props {
  onVerified: () => void;
}

const DeviceVerificationScreen: React.FC<Props> = ({ onVerified }) => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const deviceId = getDeviceId();
  const navigate = useNavigate();

  // This screen can only verify a device for a session that still exists. With
  // no token there is nothing to retry here, and the backend's own 401 copy
  // ("Missing authorization header") is meaningless to the user. Clear whatever
  // Amplify still holds and hand the reason to the login page.
  const bounceToLogin = (err: NotAuthenticatedError) => {
    signOut().catch(() => { /* best-effort */ });
    navigate("/login", { replace: true, state: { info: err.message } });
  };

  const sendOtp = async () => {
    setSending(true);
    setError(null);
    try {
      const res = await DeviceAPI.check(deviceId);
      setOtpSent(res.otp_sent || res.trusted);
      if (res.trusted) {
        onVerified();
        return;
      }
      setResendCooldown(60);
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        bounceToLogin(err);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not send verification code.";
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    sendOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleChange = (idx: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[idx] = value;
    setOtp(next);
    if (value && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (next.every(d => d) && next.join("").length === 6) {
      handleVerify(next.join(""));
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = [...otp];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setOtp(next);
    inputRefs.current[Math.min(text.length, 5)]?.focus();
    if (text.length === 6) handleVerify(text);
  };

  const handleVerify = async (code: string) => {
    if (verifying) return;
    setVerifying(true);
    setError(null);
    try {
      await DeviceAPI.verify(deviceId, code);
      onVerified();
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        bounceToLogin(err);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Verification failed.";
      setError(msg);
      setOtp(Array(6).fill(""));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7]">
      <div className="max-w-sm w-full mx-4 rounded-2xl bg-white shadow-sm border border-slate-200 p-8">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-lg bg-[#0066ff] flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2L14 5V11L8 14L2 11V5L8 2Z" stroke="white" strokeWidth="1.5" fill="none"/>
            </svg>
          </div>
          <span className="font-semibold text-slate-900 text-sm">SwasthiQ</span>
        </div>

        <h1 className="text-xl font-semibold text-slate-900 mb-1">Verify your device</h1>
        <p className="text-sm text-slate-500 mb-8">
          {otpSent
            ? "We sent a 6-digit code to your email. Enter it below to continue."
            : sending
            ? "Sending verification code…"
            : "We'll send a 6-digit code to your email."}
        </p>

        {/* OTP input row */}
        <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
          {otp.map((digit, idx) => (
            <input
              key={idx}
              ref={el => { inputRefs.current[idx] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(idx, e.target.value)}
              onKeyDown={e => handleKeyDown(idx, e)}
              disabled={verifying || !otpSent}
              className={`w-11 h-12 rounded-xl border text-center text-lg font-semibold text-slate-900
                outline-none transition-all
                ${digit ? "border-[#0066ff] bg-blue-50" : "border-slate-200 bg-slate-50"}
                focus:border-[#0066ff] focus:ring-2 focus:ring-blue-100
                disabled:opacity-40`}
            />
          ))}
        </div>

        {error && (
          <p className="text-sm text-red-600 text-center mb-4">{error}</p>
        )}

        <button
          onClick={() => handleVerify(otp.join(""))}
          disabled={otp.join("").length < 6 || verifying || !otpSent}
          className="w-full rounded-full bg-[#0066ff] py-3 text-sm font-medium text-white
            hover:bg-[#0051cc] disabled:opacity-40 disabled:cursor-not-allowed transition-colors mb-4"
        >
          {verifying ? "Verifying…" : "Continue"}
        </button>

        <div className="text-center space-y-2">
          <button
            onClick={sendOtp}
            disabled={resendCooldown > 0 || sending}
            className="text-sm text-[#0066ff] hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
          </button>
          <br />
          <button
            onClick={async () => {
              try { await signOut(); } catch { /* best-effort */ }
              window.location.href = "/login";
            }}
            className="text-sm text-slate-400 hover:text-slate-600"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeviceVerificationScreen;
